import { Outlet, createFileRoute, redirect } from "@tanstack/react-router";
import { useGetDefaultsQuery } from "api/TenantSlug/Setting/hooks";
import { AuthLayout } from "components/Layouts/AuthLayout/AuthLayout.tsx";
import { getSessionFromStorage } from "hooks/useSession";
import { toast } from "react-toastify";
import { isAccessGrantingAuthFlow } from "utils/isAccessGrantingAuthFlow.ts";
import { tenantExists } from "utils/tenantExists.ts";

export const Route = createFileRoute("/$tenantSlug/auth/_layout")({
    component: AuthLayoutComponent,
    beforeLoad: async ({ params, location, context: { t } }) => {
        const { token, user } = getSessionFromStorage();

        if (token) {
            // Already logged in: skip the login form and route straight to where this tenant slug leads.
            // user_active reflects the real membership pivot — a user who left this tenant still has it
            // in `tenants` (just inactive), and must be allowed to see the registration/confirm flow again
            // instead of being bounced straight into a tenant they no longer have active access to.
            const hasAccess = user?.tenants?.some(
                (tenant) => tenant.slug_name === params.tenantSlug && tenant.user_active !== false
            );
            if (hasAccess) {
                throw redirect({ to: "/$tenantSlug", params: { tenantSlug: params.tenantSlug }, replace: true });
            }

            if (isAccessGrantingAuthFlow(location.pathname)) {
                return;
            }
        }

        if (!(await tenantExists(params.tenantSlug))) {
            // beforeLoad can run more than once for the same visit (route re-matching while the
            // redirect below settles) — a fixed toastId stops react-toastify from stacking duplicates.
            toast.error(t("common.errors.tenant_not_found"), { toastId: "tenant-not-found" });
            throw redirect({ to: token ? "/account" : "/", replace: true });
        }

        if (token) {
            throw redirect({ to: "/$tenantSlug/no-access", params: { tenantSlug: params.tenantSlug }, replace: true });
        }
    },
});

function AuthLayoutComponent() {
    const { tenantSlug } = Route.useParams();
    const defaultsQuery = useGetDefaultsQuery(tenantSlug);

    return (
        <AuthLayout title={defaultsQuery.data?.tenant.title} logo={defaultsQuery.data?.tenant.logo ?? undefined}>
            <Outlet />
        </AuthLayout>
    );
}
